import '@/assets/css/boxicons.min.css'
import '@/assets/css/variables.css'
import '@/assets/css/global.css'
import '@/assets/css/page-transition.css'
import '@/assets/css/main.css'

import { createApp, ref } from 'vue'
import App from './app.vue'
import routerInstall from './router'
import { createPinia } from 'pinia'
import { mountMultiDragPlugin } from '@/composables/useSortable'
import { configure } from 'vue-gtag'
import { Lib } from '@/lib'
import { createCodepush, Codepush } from '@/lib/Codepush'
// vue-flow
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@/assets/css/vue-flow.scss'
// vue-shepherd
import 'shepherd.js/dist/css/shepherd.css'
import '@/assets/css/tour-guide.scss'
import Logger from '@homecheck/logger'
import { CloudFile } from './lib/CloudFile'
import { Network } from '@capacitor/network'

// 재배포 후 stale index.html이 더 이상 존재하지 않는 청크를 참조하면
// 동적 import가 text/html(MIME 오류)로 실패한다. 이 경우 한 번만 새로고침하여
// 최신 index.html과 청크를 다시 받아온다. (무한 새로고침은 10초 쿨다운으로 방지)
window.addEventListener('vite:preloadError', (event: Event): void => {
  // preventDefault를 호출하지 않으면 Vite가 에러를 다시 throw하므로,
  // 쿨다운으로 reload하지 않는 경로에서도 조용히 처리되도록 먼저 호출한다.
  event.preventDefault()
  // 네이티브 앱은 청크가 번들에 포함되어 이 오류가 사실상 발생하지 않고,
  // 웹뷰 reload도 불필요하므로 제외한다. (웹 전용 안전망)
  if (isApp()) return
  const RELOAD_KEY = 'vite-preload-reload-at'
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0')
    if (Date.now() - last < 10_000) return
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // sessionStorage 사용 불가 환경 — 쿨다운 없이 진행
  }
  window.location.reload()
})

// MultiDrag 플러그인 안전하게 마운트
mountMultiDragPlugin()

const BUILD_MODE = import.meta.env.VITE_BUILD_MODE
// 프로덕션 환경 여부
export const IS_PRODUCTION: boolean = !BUILD_MODE?.includes('dev')
// 환경에 따른 gcs 루트 경로 설정
export const GCS_ROOT_PATH: string = IS_PRODUCTION ? 'zentrix' : 'zentrix-dev'
// 앱인지 확인
export const isApp = (): boolean => {
  try {
    if (typeof window !== 'undefined' && window.Capacitor)
      return window.Capacitor.isNativePlatform()
    return false
  } catch {
    return false
  }
}

// 네트워크 상태를 저장하는 ref
export interface NetworkStatusObject {
  status: boolean
  last_connect: number
}

export const networkStatus = ref<NetworkStatusObject>({
  status: true,
  last_connect: Date.now(),
})

// 마지막 연결 시간을 localStorage에 저장하는 함수
const updateLastConnectTime = (): void => {
  const currentTime = Date.now()
  localStorage.setItem('network_last_connect', currentTime.toString())
  networkStatus.value.last_connect = currentTime
  console.info('마지막 연결 시간 업데이트:', new Date(currentTime).toLocaleString())
}

// 네트워크 상태 변경 리스너 설정
export const setupNetworkListener = async (): Promise<void> => {
  // 앱 환경에서만 실행
  if (!isApp()) {
    networkStatus.value.status = true
    updateLastConnectTime()
    return
  }

  try {
    // localStorage에서 마지막 연결 시간 가져오기
    const savedLastConnect = localStorage.getItem('network_last_connect')
    if (savedLastConnect) {
      networkStatus.value.last_connect = parseInt(savedLastConnect, 10)
    }

    // 초기 네트워크 상태 설정
    const status = await Network.getStatus()
    networkStatus.value.status = status.connected

    // 연결된 상태라면 마지막 연결 시간 업데이트
    if (status.connected) {
      updateLastConnectTime()
    }

    // 네트워크 상태 변경 리스너 추가
    Network.addListener('networkStatusChange', status => {
      networkStatus.value.status = status.connected
      console.info('네트워크 상태 변경:', status.connected ? '연결됨' : '연결 끊김')

      // 연결되었을 때 마지막 연결 시간 업데이트
      if (status.connected) {
        console.info('네트워크 연결됨 - 마지막 연결 시간 업데이트')
        updateLastConnectTime()
      }
    })

    // 연결 상태에서 주기적으로 마지막 연결 시간 업데이트 (1분마다)
    const updateInterval = setInterval(() => {
      if (networkStatus.value.status) {
        console.info('주기적 업데이트 - 마지막 연결 시간 업데이트')
        updateLastConnectTime()
      }
    }, 60 * 1000) // 1분

    // 앱이 종료될 때 인터벌 정리
    window.addEventListener('beforeunload', () => {
      clearInterval(updateInterval)
    })
  } catch (error) {
    console.error('네트워크 상태 리스너 설정 실패:', error)
    networkStatus.value.status = true // 오류 발생 시 기본값으로 설정
  }
}

// 네트워크 상태 리스너 제거
export const removeNetworkListener = (): void => {
  if (isApp()) {
    Network.removeAllListeners()
  }
}

// 코드푸쉬를 위한 앱준비 확인
Codepush.notifyAppReady()

// codepush 인스턴스
let codepushInstance: Codepush | null = null
export const getCodepushInstance = (): Codepush | null => codepushInstance

// 앱 초기화 함수
async function initializeApp(): Promise<void> {
  try {
    // 로거
    Logger.initialize({
      utc: 0,
      projectName: 'zentrix',
    })

    // 클라우드 파일
    CloudFile.setEncryptKey(import.meta.env.VITE_CLOUD_FILE_ENCRYPT_KEY)
    CloudFile.setUploadURL(import.meta.env.VITE_CLOUD_FILE_UPLOAD_URL)
    CloudFile.setConfig(
      import.meta.env.VITE_CLOUD_FILE_SIGN_NAME,
      import.meta.env.VITE_CLOUD_FILE_SIGN_KEY,
      import.meta.env.VITE_CLOUD_FILE_SIGN_URL,
    )

    // package.json에서 버전 가져오기
    const packageVersion = import.meta.env.VITE_PACKAGE_VERSION

    // 기본값 설정
    let platform: 'ios' | 'android' | 'web' = 'web'
    let packageName = 'kr.homecheck.zentrix'
    let webVersion = packageVersion
    let appVersion: string | null = null

    if (isApp()) {
      const { Device } = await import('@capacitor/device')
      const { App: CapApp } = await import('@capacitor/app')

      const deviceInfo = await Device.getInfo()
      const appInfo = await CapApp.getInfo()

      platform = deviceInfo.platform
      packageName = appInfo.id
      appVersion = appInfo.version

      try {
        // 버전 확인
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
        const currentBundle = await CapacitorUpdater.current()

        // 내장번들인 경우
        if (currentBundle.bundle.id !== 'builtin') webVersion = currentBundle.bundle.version
        // 아니면 package.json, 앱의 버전 순으로 적용
        else webVersion = packageVersion || appInfo.version
      } catch (error: unknown) {
        webVersion = packageVersion || appInfo.version
        if (error instanceof Error) console.error(error)
      }
    }

    // Lib 초기화
    await Lib.Init({
      packageName,
      platform,
      webVersion,
      appVersion,
      storagePath: 'local',
      isWeb: platform === 'web' || !isApp(),
    })

    // 네트워크 상태 리스너 설정
    await setupNetworkListener()

    // 앱 환경에서만 코드푸시 초기화
    if (isApp()) {
      // StatusBar 설정 (앱이 상태바에 가려지지 않도록)
      try {
        const { initializeStatusBar } = await import('@/app/native/statusbar')
        await initializeStatusBar()
      } catch (error) {
        Logger.error(`StatusBar 설정 실패: ${error}`)
      }

      codepushInstance = createCodepush({
        checkInterval: 0, // n분마다 체크 (0이면 자동 체크 비활성화)
      })

      // 코드푸시 초기화
      await codepushInstance.initialize()
    }
  } catch (error) {
    Logger.error(error)
  }
}

// 앱 시작 함수
async function startApp(): Promise<void> {
  // URL 형식 자동 변환
  if (typeof window !== 'undefined') {
    const currentUrl = window.location.href
    const origin = window.location.origin
    const pathname = window.location.pathname
    const search = window.location.search

    if (!IS_PRODUCTION) {
      // dev 모드 - 일반에서 해시로
      if (pathname !== '/') {
        // 해시가 없거나, 해시는 있지만 경로가 pathname에 있는 경우
        if (!currentUrl.includes('#') || currentUrl.endsWith('#/')) {
          const newUrl = `${origin}/#${pathname}${search}`
          window.location.replace(newUrl)
          return // 리다이렉트 후 앱 시작 중단
        }
      }
    } else {
      // 프로덕션 모드 - 해시에서 일반으로
      if (currentUrl.includes('#/')) {
        const hashIndex = currentUrl.indexOf('#/')
        const hashPath = currentUrl.substring(hashIndex + 1)
        if (hashPath && hashPath !== '/') {
          const newUrl = `${origin}${hashPath}`
          window.location.replace(newUrl)
          return // 리다이렉트 후 앱 시작 중단
        }
      }
    }
  }

  // 앱 초기화
  await initializeApp()

  // Vue 앱 생성
  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)

  // 프로덕션과 개발 환경에서 모두 활성화
  const gaTag = import.meta.env.VITE_GA_TAG
  if (gaTag) {
    configure({
      tagId: gaTag,
      config: {
        // 익명화된 IP 사용
        anonymize_ip: true,
        // 사용자 개인정보 보호 설정
        allow_google_signals: false,
        // 광고 맞춤설정 비활성화
        allow_ad_personalization_signals: false,
      },
    })
  }

  // 라우터 설치
  routerInstall(app)

  // 앱 마운트
  app.mount('#app')

  // 마운트 완료 후 앱 환경에서만 네이티브 버전 체크
  if (isApp()) {
    // 코드푸시 리로드 플래그 제거
    const reloadFlag = localStorage.getItem('codepush_reload_flag')
    if (reloadFlag === 'true') {
      localStorage.removeItem('codepush_reload_flag')
    }

    // 네이티브 버전 체크
    import('@/app/util').then(({ checkRequiredNativeUpdate }) => {
      checkRequiredNativeUpdate()
    })
  }
}

// 앱 시작
startApp()
